import type { DefineQueryOptions, EntryKey } from "@pinia/colada";
import { type MaybeRefOrGetter, toValue } from "vue";

import QuerySnippetsService, {
  type GetQuerySnippetsParams,
  type GetQuerySnippetsResponse,
} from "shared/modules/QuerySnippets/services/QuerySnippetsService";
import type {
  Maybe,
  Nullable,
  QuerySnippetDetails,
  QuerySnippetType,
} from "shared/types";

const QUERY_SNIPPETS_QUERY_KEY = "query-snippets";

const QUERY_SNIPPETS_QUERY_OPTIONS = {
  gcTime: 1_800_000,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
  staleTime: 300_000,
};

type QuerySnippetDetailQueryKey = readonly [
  typeof QUERY_SNIPPETS_QUERY_KEY,
  QuerySnippetType,
  "detail",
  Nullable<number>,
];

type QuerySnippetsListQueryKey = readonly [
  typeof QUERY_SNIPPETS_QUERY_KEY,
  QuerySnippetType,
  "list",
  GetQuerySnippetsParams,
];

export const querySnippetsQueryKeys = {
  all: (): EntryKey => [QUERY_SNIPPETS_QUERY_KEY],
  detail: (
    type: QuerySnippetType,
    id: Nullable<number>
  ): QuerySnippetDetailQueryKey => [
    QUERY_SNIPPETS_QUERY_KEY,
    type,
    "detail",
    id,
  ],
  details: (type: QuerySnippetType): EntryKey => [
    QUERY_SNIPPETS_QUERY_KEY,
    type,
    "detail",
  ],
  list: (
    type: QuerySnippetType,
    params: GetQuerySnippetsParams
  ): QuerySnippetsListQueryKey => [
    QUERY_SNIPPETS_QUERY_KEY,
    type,
    "list",
    params,
  ],
  lists: (type: QuerySnippetType): EntryKey => [
    QUERY_SNIPPETS_QUERY_KEY,
    type,
    "list",
  ],
};

export function querySnippetsListQueryOptions(
  querySnippetType: MaybeRefOrGetter<QuerySnippetType>,
  requestParams: MaybeRefOrGetter<GetQuerySnippetsParams>
): () => DefineQueryOptions<GetQuerySnippetsResponse> {
  return () => {
    const querySnippetTypeSnapshot = toValue(querySnippetType);
    const requestParamsSnapshot = { ...toValue(requestParams) };

    return {
      ...QUERY_SNIPPETS_QUERY_OPTIONS,
      key: querySnippetsQueryKeys.list(
        querySnippetTypeSnapshot,
        requestParamsSnapshot
      ),
      query: ({ entry, signal }) => {
        const [, queryType, , params] = entry.key as QuerySnippetsListQueryKey;

        return QuerySnippetsService.getQuerySnippets(queryType, params, {
          signal,
        });
      },
    };
  };
}

export function querySnippetDetailQueryOptions(
  querySnippetType: MaybeRefOrGetter<QuerySnippetType>,
  querySnippetId: MaybeRefOrGetter<Maybe<number>>
): () => DefineQueryOptions<QuerySnippetDetails> {
  return () => {
    const querySnippetIdSnapshot = toValue(querySnippetId);
    const querySnippetTypeSnapshot = toValue(querySnippetType);

    return {
      ...QUERY_SNIPPETS_QUERY_OPTIONS,
      enabled: querySnippetIdSnapshot !== undefined,
      key: querySnippetsQueryKeys.detail(
        querySnippetTypeSnapshot,
        querySnippetIdSnapshot ?? null
      ),
      query: ({ entry, signal }) => {
        const [, queryType, , id] = entry.key as QuerySnippetDetailQueryKey;

        if (id === null) {
          throw new Error("A query snippet ID is required to load its details");
        }

        return QuerySnippetsService.getQuerySnippet(queryType, id, { signal });
      },
    };
  };
}
